import { useMemo } from "react";
import { FALLBACK_OUTLINE, COURSE_SCHEDULE } from "../course/outline";
import { Blocks } from "./Blocks";
import { PresetShell } from "./PresetShell";
import {
  compactBlocks,
  createAtom,
  readNumber,
  readObjects,
  readRecord,
  readStatus,
  readString,
  readStringArray,
} from "./data";
import type { PresetViewProps } from "./CourseOverview";

/**
 * Tablero de una unidad: en qué punto va el estudiante dentro de ella y qué le
 * toca ahora. Lo que manda es el hito siguiente, así que va destacado.
 */

function moduleKey(value: string): string {
  return value.replace(/[^0-9]/g, "");
}

function scheduleDescription(moduleCode: string): string {
  const week = COURSE_SCHEDULE.find((item) => moduleKey(item.duration) === moduleKey(moduleCode));
  return week ? week.description : "";
}

export function UnitDashboard({ preset, data, emit, interactive }: PresetViewProps) {
  const unitCode = readString(data, "unit") ?? "U1";
  const unitTitle = readString(data, "title");
  const nextMilestone = readString(data, "next_milestone");
  const progress = readRecord(data, "progress");

  const declaredModules = useMemo(() => readObjects(data, "modules"), [data]);

  const modules = useMemo(() => {
    if (declaredModules.length > 0) {
      return declaredModules.map((module, index) => ({
        code: readString(module, "code") ?? `M${index + 1}`,
        title: readString(module, "title") ?? `Módulo ${index + 1}`,
        topics: readStringArray(module, "topics"),
        status: readStatus(module),
        evaluation: readString(module, "evaluation"),
      }));
    }
    // Sin módulos declarados se cae a la unidad equivalente del sílabo.
    const number = moduleKey(unitCode);
    const fallbackUnit = FALLBACK_OUTLINE.units.find((unit) => moduleKey(unit.unit) === number);
    return (fallbackUnit?.modules ?? []).map((module, index) => ({
      code: `M${module.module}`,
      title: module.title,
      topics: module.topics,
      status: readStatus({ module: index }),
      evaluation: null,
    }));
  }, [declaredModules, unitCode]);

  const completed = readNumber(progress ?? {}, "completed") ?? modules.filter((module) => module.status === "done").length;
  const total = readNumber(progress ?? {}, "total") ?? modules.length;
  const current = modules.find((module) => module.status === "current") ?? modules[0];

  const blocks = compactBlocks([
    current
      ? createAtom("metric_cards", {
          meta: { title: "Dónde estás" },
          cards: [
            {
              label: "Módulos cerrados",
              value: `${completed}`,
              unit: `de ${total}`,
              hint: unitTitle ?? unitCode,
            },
            {
              label: "Módulo en curso",
              value: current.code,
              hint: current.title,
            },
            {
              label: "Temas del módulo actual",
              value: String(current.topics.length || 3),
              hint: "Antes y durante la sesión",
            },
          ],
        })
      : null,

    createAtom("timeline", {
      meta: { title: `${unitCode} paso a paso`, subtitle: "Módulos y estado" },
      orientation: "vertical",
      items: modules.map((module) => ({
        label: module.code,
        title: module.title,
        description: module.topics.length > 0 ? module.topics.join(" · ") : scheduleDescription(module.code),
        duration: module.evaluation ? `Evaluación: ${module.evaluation}` : null,
        tags: module.evaluation ? [module.evaluation] : [],
        status: module.status,
      })),
    }),

    nextMilestone
      ? createAtom("callout", {
          variant: "warning",
          title: "Próximo hito",
          body: nextMilestone,
        })
      : null,

    createAtom("callout", {
      variant: "tip",
      title: "Antes de la sesión",
      body: "Lee el material del módulo en curso y anota las dudas. El asistente puede resolverte un caso resuelto paso a paso o dejarte un simulador para probar los supuestos antes de clase.",
    }),
  ]);

  return (
    <PresetShell
      eyebrow={unitCode}
      title={preset.title ?? unitTitle ?? `Unidad ${unitCode}`}
      lead={
        unitTitle
          ? `${unitTitle} · ${modules.length} ${modules.length === 1 ? "módulo" : "módulos"}`
          : `${modules.length} ${modules.length === 1 ? "módulo" : "módulos"} en esta unidad`
      }
      chips={[
        { icon: "layers", label: `${completed}/${total} módulos` },
        ...(current ? [{ icon: "play" as const, label: `En curso: ${current.code}` }] : []),
      ]}
    >
      <Blocks blocks={blocks} emit={emit} interactive={interactive} />
    </PresetShell>
  );
}

export default UnitDashboard;
